import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Segmented } from 'antd';
import { DownloadOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CountUp from 'react-countup';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import { getVentesRapport } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeStatusBadge, NdoeImage, NdoeError, NdoeLoading,
  useFicheArticle,
} from '../../components/ndoe';
// Pont jetons CSS → canvas : un <canvas> ne comprend pas var(--ndoe-gold), il
// faut donc lui donner la valeur lue à l'exécution sur :root.
import { jetons, alpha, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import { toCsv, downloadCsv } from './exportCsv';
import { money, pct, dateTxt } from './format';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

// Delta vs période précédente : flèche + couleur, « — » quand il n'y a pas de
// base de comparaison (nouvelle marque, période vide).
const Delta = ({ value, suffix = '%' }) => {
  if (value === null || value === undefined) return <span className="rv-delta rv-delta--na">—</span>;
  const up = value > 0;
  const flat = value === 0;
  return (
    <span className={`rv-delta ${flat ? '' : up ? 'is-plus' : 'is-moins'}`}>
      {flat ? '=' : up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(value)}{suffix}
    </span>
  );
};

const VUES = [
  { value: 'marques', label: 'Marques' },
  { value: 'categories', label: 'Catégories' },
  { value: 'produits', label: 'Top produits' },
];

export default function RapportVentes() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();
  const { openFiche, ficheElement } = useFicheArticle();

  const [period, setPeriod] = useState({ period: 30 });
  const [vue, setVue] = useState('marques');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getVentesRapport(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport des ventes.');
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const handleExport = () => {
    if (!payload) return;
    setExporting(true);
    try {
      const blocs = [
        ...payload.marques.map((m) => ({ type: 'Marque', nom: m.marque, ca: m.ca, paires: m.paires, part_pct: m.part_pct, delta_pct: m.delta_pct })),
        ...payload.categories.map((c) => ({ type: 'Catégorie', nom: c.categorie, ca: c.ca, paires: c.paires, part_pct: c.part_pct, delta_pct: c.delta_pct })),
        ...payload.top_produits.map((p) => ({ type: 'Produit', nom: `${p.nom_produit}${p.code_produit ? ` (${p.code_produit})` : ''}`, ca: p.ca, paires: p.paires, part_pct: p.part_pct, delta_pct: '' })),
        ...payload.serie_jour.map((j) => ({ type: 'Jour', nom: j.date, ca: j.ca, paires: j.paires, part_pct: '', delta_pct: '' })),
      ];
      const columns = [
        { title: 'Type', value: (r) => r.type },
        { title: 'Libellé', value: (r) => r.nom },
        { title: 'CA ($)', value: (r) => r.ca },
        { title: 'Paires', value: (r) => r.paires },
        { title: 'Part du CA (%)', value: (r) => r.part_pct },
        { title: 'Évolution vs période précédente (%)', value: (r) => (r.delta_pct === null ? 'nouveau' : r.delta_pct) },
      ];
      downloadCsv(`ventes_${payload.meta.start_date}_${payload.meta.end_date}.csv`, toCsv(columns, blocs));
      toast.success('Rapport exporté.');
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const kpis = payload?.kpis || {};
  const deltas = kpis.deltas || {};
  const highlights = payload?.highlights || {};
  const meta = payload?.meta || {};

  // --- Courbe CA : période courante (or, dégradé) vs précédente (pointillés) ----
  // Jetons lus ICI, dans le corps du rendu : au chargement du module le CSS
  // n'est pas garanti appliqué, getComputedStyle rendrait du vide et le pont
  // retomberait EN SILENCE sur ses valeurs de repli.
  const t = jetons();
  const chartData = payload ? {
    labels: payload.serie_jour.map((j) => j.date.slice(8, 10) + '/' + j.date.slice(5, 7)),
    datasets: [
      {
        label: 'CA',
        // L'or de la charte porte le CA de la période courante : c'est la série
        // que l'œil doit trouver en premier.
        data: payload.serie_jour.map((j) => j.ca),
        borderColor: t.gold,
        borderWidth: 2.5,
        pointRadius: payload.serie_jour.length > 40 ? 0 : 3,
        pointBackgroundColor: t.gold,
        tension: 0.35,
        fill: true,
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          // Au tout premier passage chartArea est encore inconnue : un aplat
          // translucide évite un dataset sans couleur le temps d'une frame.
          if (!chartArea) return alpha(t.gold, 0.12);
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, alpha(t.gold, 0.28));
          g.addColorStop(1, alpha(t.gold, 0.02));
          return g;
        },
      },
      {
        // Repère de comparaison, pas une donnée à lire : gris atténué et
        // pointillés le font passer derrière la courbe courante.
        label: 'Période précédente',
        data: payload.serie_jour_precedente.map((j) => j.ca),
        borderColor: alpha(t.muted, 0.55),
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 0,
        tension: 0.35,
        fill: false,
      },
    ],
  } : null;

  // optionsBase apporte la grille, les axes, la légende et l'info-bulle aux
  // couleurs de la charte, plus une animation sobre (420 ms au lieu de 900,
  // coupée si l'utilisateur demande moins de mouvement au niveau système).
  // fusionner remplace `ticks` en bloc : la couleur y est donc redonnée avec
  // les réglages propres à ce graphique.
  const chartOptions = fusionner(optionsBase(), {
    plugins: {
      tooltip: {
        callbacks: {
          label: (item) => ` ${item.dataset.label} : ${Number(item.parsed.y).toLocaleString('fr-FR')} $`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 12, color: t.muted, font: { size: 10.5 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: t.hairline, drawTicks: false },
        ticks: { color: t.muted, font: { size: 10.5 }, padding: 6, callback: (v) => `${v} $` },
        border: { display: false },
      },
    },
  });

  // Barres de classement : la part du CA donne la largeur ; le clic interconnecte.
  const barres = (rows, cle, onClick) => {
    const max = rows.length ? rows[0].ca : 0;
    return (
      <div className="rv-barres">
        {rows.slice(0, 12).map((r) => (
          <button type="button" className="rv-barre" key={r[cle]} onClick={() => onClick(r)}
            title="Voir la performance détaillée">
            <span className="rv-barre__nom">{r[cle]}</span>
            <span className="rv-barre__track" aria-hidden="true">
              <i style={{ width: `${max ? Math.max(3, (r.ca / max) * 100) : 0}%` }} />
            </span>
            <span className="rv-barre__val num">{money(r.ca)}</span>
            <span className="rv-barre__part num">{pct(r.part_pct)}</span>
            <Delta value={r.delta_pct} />
          </button>
        ))}
      </div>
    );
  };

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Ventes"
        subtitle="Le chiffre, sa dynamique et ce qui le porte — comparé à la période précédente."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">
            du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)} · comparé du {dateTxt(meta.periode_precedente?.start_date)} au {dateTxt(meta.periode_precedente?.end_date)}
          </span>
        )}
      </div>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading || !payload ? (
        <NdoeLoading rows={6} />
      ) : (
        <>
          <div className="rv-kpis">
            <div className="rv-kpi rv-kpi--gold">
              <small>Chiffre d'affaires</small>
              <b className="num"><CountUp end={kpis.chiffre_affaires} duration={0.9} separator=" " decimals={kpis.chiffre_affaires % 1 ? 2 : 0} /> $</b>
              <Delta value={deltas.chiffre_affaires_pct} />
            </div>
            <div className="rv-kpi">
              <small>Paires vendues</small>
              <b className="num"><CountUp end={kpis.paires_vendues} duration={0.9} /></b>
              <Delta value={deltas.paires_pct} />
            </div>
            <div className="rv-kpi">
              <small>Commandes</small>
              <b className="num"><CountUp end={kpis.commandes} duration={0.9} /></b>
            </div>
            <div className="rv-kpi">
              <small>Panier moyen</small>
              <b className="num">{money(kpis.panier_moyen)}</b>
              <Delta value={deltas.panier_pct} />
            </div>
            <div className="rv-kpi">
              <small>{kpis.marge_couverture_pct ? `Marge (sur ${kpis.marge_couverture_pct} % du CA)` : 'Marge estimée'}</small>
              <b className="num">{kpis.marge_estimee !== null && kpis.marge_estimee !== undefined ? money(kpis.marge_estimee) : '—'}</b>
            </div>
          </div>

          <div className="rv-highlights">
            {highlights.meilleur_jour && (
              <div className="rv-hl">
                <NdoeStatusBadge kind="custom" tone="gold" label="Meilleur jour" />
                <b>{dateTxt(highlights.meilleur_jour.date)}</b>
                <small className="num">{money(highlights.meilleur_jour.ca)} · {highlights.meilleur_jour.paires} paire(s)</small>
              </div>
            )}
            {highlights.tendance && (
              <div className="rv-hl">
                <NdoeStatusBadge kind="custom"
                  tone={highlights.tendance.sens === 'hausse' ? 'ok' : highlights.tendance.sens === 'baisse' ? 'danger' : 'neutral'}
                  label={`Tendance ${highlights.tendance.sens}`} />
                <b>{highlights.tendance.pct === null ? 'démarrage' : `${highlights.tendance.pct > 0 ? '+' : ''}${highlights.tendance.pct} %`}</b>
                <small>seconde moitié de période vs première</small>
              </div>
            )}
            {highlights.moteur && (
              <div className="rv-hl">
                <NdoeStatusBadge kind="custom" tone="info" label="Moteur de croissance" />
                <b>{highlights.moteur.marque}</b>
                <small className="num">+{highlights.moteur.delta_pct} % · {pct(highlights.moteur.part_pct)} du CA</small>
              </div>
            )}
          </div>

          <section className="ndoe-card rv-chart">
            <div className="ndoe-section__title">Chiffre d'affaires quotidien</div>
            <div className="rv-chart__zone">
              <Line data={chartData} options={chartOptions} />
            </div>
          </section>

          <div className="rv-vue">
            <Segmented options={VUES} value={vue} onChange={setVue} />
          </div>

          {vue === 'marques' && barres(payload.marques, 'marque',
            (r) => navigate(`/rapports/performance${r.id_marque ? `?marque=${r.id_marque}` : ''}`))}
          {vue === 'categories' && barres(payload.categories, 'categorie',
            (r) => navigate(`/rapports/performance${r.id_categorie ? `?categorie=${r.id_categorie}` : ''}`))}
          {vue === 'produits' && (
            <div className="rv-produits">
              {payload.top_produits.map((p, i) => (
                <button type="button" className="rv-produit" key={p.id_produit} onClick={() => openFiche(p.id_produit)}>
                  <span className="rv-produit__rang num">{i + 1}</span>
                  <NdoeImage src={p.img} alt={p.nom_produit} size="md" preview={false} />
                  <span className="rv-produit__txt">
                    <b>{p.nom_produit}</b>
                    <small>{p.code_produit || '—'}{p.marque ? ` · ${p.marque}` : ''}</small>
                  </span>
                  <span className="rv-produit__chiffres">
                    <b className="num">{money(p.ca)}</b>
                    <small className="num">{p.paires} paire(s) · {pct(p.part_pct)} du CA</small>
                  </span>
                </button>
              ))}
            </div>
          )}

          <p className="pp-note">
            <NdoeStatusBadge kind="custom" tone="info" label="Lecture" />
            {' '}Ventes NETTES des retours, même comptage que tout le module Rapports. Un clic sur une
            marque ou une catégorie ouvre la performance produit filtrée ; un clic sur un produit ouvre
            sa fiche. Le détail vente par vente reste dans «&nbsp;Ventes confirmées&nbsp;».
          </p>
        </>
      )}
      {ficheElement}
    </NdoePage>
  );
}
